<script setup>
import { ref, computed, onBeforeMount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const store = useStore();
const getters = useStoreGetters();

const records = computed(
  () => getters['classifications/getClassifications'].value
);
const uiFlags = computed(() => getters['classifications/getUIFlags'].value);

const loading = ref({});
const showAddPopup = ref(false);
const showEditPopup = ref(false);
const showDeleteConfirmationPopup = ref(false);
const selectedClassification = ref({});

// Form state
const addForm = ref({
  name: '',
  description: '',
  is_active: true,
  hide: false,
  enable_auto_dm_negative: false,
  send_proactive_comment: false,
});
const editForm = ref({
  id: null,
  name: '',
  description: '',
  is_active: true,
  hide: false,
  enable_auto_dm_negative: false,
  send_proactive_comment: false,
});

const formErrors = ref({
  name: '',
});

const resetForm = formRef => {
  formErrors.value = { name: '' };
  formRef.value = {
    id: null,
    name: '',
    description: '',
    is_active: true,
    hide: false,
    enable_auto_dm_negative: false,
    send_proactive_comment: false,
  };
};

const validateForm = formData => {
  let valid = true;
  formErrors.value = { name: '' };

  if (!formData.name || !formData.name.trim()) {
    formErrors.value.name = t('CLASSIFICATION_MGMT.FORM.NAME.REQUIRED');
    valid = false;
  } else if (formData.name.length > 255) {
    formErrors.value.name = t('CLASSIFICATION_MGMT.FORM.NAME.MAX', {
      count: 255,
    });
    valid = false;
  }

  return valid;
};

const openAddPopup = () => {
  resetForm(addForm);
  showAddPopup.value = true;
};
const hideAddPopup = () => {
  showAddPopup.value = false;
};

const openEditPopup = record => {
  editForm.value = {
    id: record.id,
    name: record.name,
    description: record.description || '',
    is_active: !!record.is_active,
    hide: !!record.hide,
    enable_auto_dm_negative: !!record.enable_auto_dm_negative,
    send_proactive_comment: !!record.send_proactive_comment,
  };
  showEditPopup.value = true;
};
const hideEditPopup = () => {
  showEditPopup.value = false;
};

const openDeletePopup = record => {
  selectedClassification.value = record;
  showDeleteConfirmationPopup.value = true;
};
const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const deleteMessage = computed(() => ` ${selectedClassification.value.name}?`);

const createClassification = async () => {
  if (!validateForm(addForm.value)) return;

  try {
    await store.dispatch('classifications/create', {
      name: addForm.value.name.trim(),
      description: addForm.value.description?.trim() || '',
      is_active: addForm.value.is_active,
      hide: addForm.value.hide,
      enable_auto_dm_negative: addForm.value.enable_auto_dm_negative,
      send_proactive_comment: addForm.value.send_proactive_comment,
    });
    useAlert(t('CLASSIFICATION_MGMT.CREATE.API.SUCCESS_MESSAGE'));
    hideAddPopup();
  } catch (error) {
    const errorMessage =
      error?.message || t('CLASSIFICATION_MGMT.CREATE.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

const updateClassification = async () => {
  if (!validateForm(editForm.value)) return;

  try {
    const {
      id,
      name,
      description,
      is_active,
      hide,
      enable_auto_dm_negative,
      send_proactive_comment,
    } = editForm.value;
    await store.dispatch('classifications/update', {
      id,
      name: name.trim(),
      description: description?.trim() || '',
      is_active,
      hide,
      enable_auto_dm_negative,
      send_proactive_comment,
    });
    useAlert(t('CLASSIFICATION_MGMT.UPDATE.API.SUCCESS_MESSAGE'));
    hideEditPopup();
  } catch (error) {
    const errorMessage =
      error?.message || t('CLASSIFICATION_MGMT.UPDATE.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

const confirmDeletion = async () => {
  loading.value[selectedClassification.value.id] = true;
  closeDeletePopup();
  try {
    await store.dispatch(
      'classifications/delete',
      selectedClassification.value.id
    );
    useAlert(t('CLASSIFICATION_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    const errorMessage =
      error?.message || t('CLASSIFICATION_MGMT.DELETE.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  } finally {
    loading.value[selectedClassification.value.id] = false;
  }
};

const tableHeaders = computed(() => [
  t('CLASSIFICATION_MGMT.LIST.TABLE_HEADER.NAME'),
  t('CLASSIFICATION_MGMT.LIST.TABLE_HEADER.DESCRIPTION'),
  t('CLASSIFICATION_MGMT.LIST.TABLE_HEADER.STATUS'),
  t('CLASSIFICATION_MGMT.FORM.HIDE.LABEL'),
  t('CLASSIFICATION_MGMT.FORM.ENABLE_AUTO_DM_NEGATIVE.LABEL'),
  t('CLASSIFICATION_MGMT.FORM.SEND_PROACTIVE_COMMENT.LABEL'),
]);

onBeforeMount(() => {
  store.dispatch('classifications/get');
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('CLASSIFICATION_MGMT.LOADING')"
    :no-records-found="!records.length"
    :no-records-message="$t('CLASSIFICATION_MGMT.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('CLASSIFICATION_MGMT.HEADER')"
        :description="$t('CLASSIFICATION_MGMT.DESCRIPTION')"
        :link-text="$t('CLASSIFICATION_MGMT.LEARN_MORE')"
        feature-name="classifications"
      >
        <template #actions>
          <Button
            icon="i-lucide-circle-plus"
            :label="$t('CLASSIFICATION_MGMT.HEADER_BTN_TXT')"
            :is-loading="uiFlags.isCreating"
            @click="openAddPopup"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-n-weak">
          <thead>
            <tr>
              <th
                v-for="thHeader in tableHeaders"
                :key="thHeader"
                class="py-4 font-semibold text-left ltr:pr-4 rtl:pl-4 text-n-slate-11"
              >
                {{ thHeader }}
              </th>
              <th class="py-4 font-semibold text-right text-n-slate-11">
                {{ $t('TABLE.HEADER.ACTIONS') }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-weak text-n-slate-12">
            <tr v-for="item in records" :key="item.id">
              <td class="py-4 ltr:pr-4 rtl:pl-4">
                <span class="mb-1 font-medium break-words text-n-slate-12">
                  {{ item.name }}
                </span>
              </td>
              <td class="py-4 ltr:pr-4 rtl:pl-4">
                <span class="text-n-slate-12 break-words">
                  {{ item.description }}
                </span>
              </td>
              <td class="py-4 ltr:pr-4 rtl:pl-4">
                <span
                  class="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium"
                  :class="
                    item.is_active
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-gray-100 text-gray-700'
                  "
                >
                  {{
                    item.is_active
                      ? $t('CLASSIFICATION_MGMT.STATUS.ACTIVE')
                      : $t('CLASSIFICATION_MGMT.STATUS.INACTIVE')
                  }}
                </span>
              </td>
              <td class="py-4 ltr:pr-4 rtl:pl-4">
                <span
                  class="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium"
                  :class="
                    item.hide
                      ? 'bg-amber-100 text-amber-700'
                      : 'bg-gray-100 text-gray-700'
                  "
                >
                  {{ item.hide ? 'Sim' : 'Não' }}
                </span>
              </td>
              <td class="py-4 ltr:pr-4 rtl:pl-4">
                <span
                  class="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium"
                  :class="
                    item.enable_auto_dm_negative
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-700'
                  "
                >
                  {{ item.enable_auto_dm_negative ? 'Sim' : 'Não' }}
                </span>
              </td>
              <td class="py-4 ltr:pr-4 rtl:pl-4">
                <span
                  class="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium"
                  :class="
                    item.send_proactive_comment
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-700'
                  "
                >
                  {{ item.send_proactive_comment ? 'Sim' : 'Não' }}
                </span>
              </td>
              <td class="py-4 min-w-xs">
                <div class="flex gap-1 justify-end">
                  <Button
                    v-tooltip.top="$t('CLASSIFICATION_MGMT.FORM.EDIT')"
                    icon="i-lucide-pen"
                    slate
                    xs
                    faded
                    :is-loading="loading[item.id]"
                    @click="openEditPopup(item)"
                  />
                  <Button
                    v-tooltip.top="$t('CLASSIFICATION_MGMT.FORM.DELETE')"
                    icon="i-lucide-trash-2"
                    xs
                    ruby
                    faded
                    :is-loading="loading[item.id]"
                    @click="openDeletePopup(item)"
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>

    <!-- Add Modal -->
    <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
      <div class="p-4">
        <h3 class="text-base font-semibold mb-4">
          {{ $t('CLASSIFICATION_MGMT.FORM.ADD.TITLE') }}
        </h3>
        <form class="space-y-4" @submit.prevent="createClassification">
          <div>
            <label class="block text-sm font-medium mb-1">
              {{ $t('CLASSIFICATION_MGMT.FORM.NAME.LABEL') }}
            </label>
            <input
              v-model="addForm.name"
              type="text"
              class="w-full rounded border border-n-weak bg-white px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-primary-500"
              :placeholder="$t('CLASSIFICATION_MGMT.FORM.NAME.PLACEHOLDER')"
              required
            />
            <p v-if="formErrors.name" class="mt-1 text-xs text-ruby-600">
              {{ formErrors.name }}
            </p>
          </div>

          <div>
            <label class="block text-sm font-medium mb-1">
              {{ $t('CLASSIFICATION_MGMT.FORM.DESCRIPTION.LABEL') }}
            </label>
            <textarea
              v-model="addForm.description"
              rows="3"
              :placeholder="
                $t('CLASSIFICATION_MGMT.FORM.DESCRIPTION.PLACEHOLDER')
              "
            />
          </div>

          <div class="flex items-center gap-2">
            <input
              id="add_is_active"
              v-model="addForm.is_active"
              type="checkbox"
              class="h-4 w-4 rounded border-n-weak text-primary-600 focus:ring-primary-500"
            />
            <label for="add_is_active" class="text-sm">
              {{ $t('CLASSIFICATION_MGMT.FORM.IS_ACTIVE.LABEL') }}
            </label>
          </div>

          <div class="flex flex-col gap-1">
            <div class="flex items-center gap-2">
              <input
                id="add_hide"
                v-model="addForm.hide"
                type="checkbox"
                class="h-4 w-4 rounded border-n-weak text-primary-600 focus:ring-primary-500"
              />
              <label for="add_hide" class="text-sm">
                {{ $t('CLASSIFICATION_MGMT.FORM.HIDE.LABEL') }}
              </label>
            </div>
            <p class="text-xs text-n-slate-11 ml-6">
              {{ $t('CLASSIFICATION_MGMT.FORM.HIDE.HELP') }}
            </p>
          </div>

          <div class="flex flex-col gap-1">
            <div class="flex items-center gap-2">
              <input
                id="add_enable_auto_dm_negative"
                v-model="addForm.enable_auto_dm_negative"
                type="checkbox"
                class="h-4 w-4 rounded border-n-weak text-primary-600 focus:ring-primary-500"
              />
              <label for="add_enable_auto_dm_negative" class="text-sm">
                {{
                  $t('CLASSIFICATION_MGMT.FORM.ENABLE_AUTO_DM_NEGATIVE.LABEL')
                }}
              </label>
            </div>
            <p class="text-xs text-n-slate-11 ml-6">
              {{ $t('CLASSIFICATION_MGMT.FORM.ENABLE_AUTO_DM_NEGATIVE.HELP') }}
            </p>
          </div>

          <div class="flex flex-col gap-1">
            <div class="flex items-center gap-2">
              <input
                id="add_send_proactive_comment"
                v-model="addForm.send_proactive_comment"
                type="checkbox"
                class="h-4 w-4 rounded border-n-weak text-primary-600 focus:ring-primary-500"
              />
              <label for="add_send_proactive_comment" class="text-sm">
                {{
                  $t('CLASSIFICATION_MGMT.FORM.SEND_PROACTIVE_COMMENT.LABEL')
                }}
              </label>
            </div>
            <p class="text-xs text-n-slate-11 ml-6">
              {{ $t('CLASSIFICATION_MGMT.FORM.SEND_PROACTIVE_COMMENT.HELP') }}
            </p>
          </div>

          <div class="flex justify-end gap-2 pt-2">
            <Button
              slate
              :label="$t('CLASSIFICATION_MGMT.FORM.CANCEL')"
              @click="hideAddPopup"
            />
            <Button
              primary
              :label="$t('CLASSIFICATION_MGMT.FORM.SAVE')"
              :is-loading="uiFlags.isCreating"
              type="submit"
            />
          </div>
        </form>
      </div>
    </woot-modal>

    <!-- Edit Modal -->
    <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
      <div class="p-4">
        <h3 class="text-base font-semibold mb-4">
          {{ $t('CLASSIFICATION_MGMT.FORM.EDIT.TITLE') }}
        </h3>
        <form class="space-y-4" @submit.prevent="updateClassification">
          <div>
            <label class="block text-sm font-medium mb-1">
              {{ $t('CLASSIFICATION_MGMT.FORM.NAME.LABEL') }}
            </label>
            <input
              v-model="editForm.name"
              type="text"
              class="w-full rounded border border-n-weak bg-white px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-primary-500"
              :placeholder="$t('CLASSIFICATION_MGMT.FORM.NAME.PLACEHOLDER')"
              required
            />
            <p v-if="formErrors.name" class="mt-1 text-xs text-ruby-600">
              {{ formErrors.name }}
            </p>
          </div>

          <div>
            <label class="block text-sm font-medium mb-1">
              {{ $t('CLASSIFICATION_MGMT.FORM.DESCRIPTION.LABEL') }}
            </label>
            <textarea
              v-model="editForm.description"
              rows="3"
              :placeholder="
                $t('CLASSIFICATION_MGMT.FORM.DESCRIPTION.PLACEHOLDER')
              "
            />
          </div>

          <div class="flex items-center gap-2">
            <input
              id="edit_is_active"
              v-model="editForm.is_active"
              type="checkbox"
              class="h-4 w-4 rounded border-n-weak text-primary-600 focus:ring-primary-500"
            />
            <label for="edit_is_active" class="text-sm">
              {{ $t('CLASSIFICATION_MGMT.FORM.IS_ACTIVE.LABEL') }}
            </label>
          </div>

          <div class="flex flex-col gap-1">
            <div class="flex items-center gap-2">
              <input
                id="edit_hide"
                v-model="editForm.hide"
                type="checkbox"
                class="h-4 w-4 rounded border-n-weak text-primary-600 focus:ring-primary-500"
              />
              <label for="edit_hide" class="text-sm">
                {{ $t('CLASSIFICATION_MGMT.FORM.HIDE.LABEL') }}
              </label>
            </div>
            <p class="text-xs text-n-slate-11 ml-6">
              {{ $t('CLASSIFICATION_MGMT.FORM.HIDE.HELP') }}
            </p>
          </div>

          <div class="flex flex-col gap-1">
            <div class="flex items-center gap-2">
              <input
                id="edit_enable_auto_dm_negative"
                v-model="editForm.enable_auto_dm_negative"
                type="checkbox"
                class="h-4 w-4 rounded border-n-weak text-primary-600 focus:ring-primary-500"
              />
              <label for="edit_enable_auto_dm_negative" class="text-sm">
                {{
                  $t('CLASSIFICATION_MGMT.FORM.ENABLE_AUTO_DM_NEGATIVE.LABEL')
                }}
              </label>
            </div>
            <p class="text-xs text-n-slate-11 ml-6">
              {{ $t('CLASSIFICATION_MGMT.FORM.ENABLE_AUTO_DM_NEGATIVE.HELP') }}
            </p>
          </div>

          <div class="flex flex-col gap-1">
            <div class="flex items-center gap-2">
              <input
                id="edit_send_proactive_comment"
                v-model="editForm.send_proactive_comment"
                type="checkbox"
                class="h-4 w-4 rounded border-n-weak text-primary-600 focus:ring-primary-500"
              />
              <label for="edit_send_proactive_comment" class="text-sm">
                {{
                  $t('CLASSIFICATION_MGMT.FORM.SEND_PROACTIVE_COMMENT.LABEL')
                }}
              </label>
            </div>
            <p class="text-xs text-n-slate-11 ml-6">
              {{ $t('CLASSIFICATION_MGMT.FORM.SEND_PROACTIVE_COMMENT.HELP') }}
            </p>
          </div>

          <div class="flex justify-end gap-2 pt-2">
            <Button
              slate
              :label="$t('CLASSIFICATION_MGMT.FORM.CANCEL')"
              @click="hideEditPopup"
            />
            <Button
              primary
              :label="$t('CLASSIFICATION_MGMT.FORM.UPDATE')"
              :is-loading="uiFlags.isUpdating"
              type="submit"
            />
          </div>
        </form>
      </div>
    </woot-modal>

    <!-- Delete Modal -->
    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('CLASSIFICATION_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('CLASSIFICATION_MGMT.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="$t('CLASSIFICATION_MGMT.DELETE.CONFIRM.YES')"
      :reject-text="$t('CLASSIFICATION_MGMT.DELETE.CONFIRM.NO')"
    />
  </SettingsLayout>
</template>
